/* ============================================================
   tabbed-collections.js
   Custom element: <tabbed-collections>
   - Tab switching with animated underline
   - Per-panel horizontal slider, card widths set by JS
   ============================================================ */

class TabbedCollections extends HTMLElement {
  connectedCallback() {
    this._index     = 0;
    this._tabs      = Array.from(this.querySelectorAll('.tabbed-collections__tab'));
    this._panels    = Array.from(this.querySelectorAll('.tabbed-collections__panel'));
    this._underline = this.querySelector('.tabbed-collections__underline');
    this._tabsNav   = this.querySelector('.tabbed-collections__tabs');

    // Read column count from CSS variable set by Liquid inline style
    this._columns = parseInt(
      getComputedStyle(this.closest('.tabbed-collections-wrapper'))
        .getPropertyValue('--tc-columns').trim() || '4'
    , 10);

    // Tab click handlers
    this._tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this._activateTab(i));
    });

    // Arrow-key navigation (ARIA tabs pattern)
    this._tabsNav && this._tabsNav.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        this._activateTab((this._index + 1) % this._tabs.length);
        this._tabs[this._index].focus();
      } else if (e.key === 'ArrowLeft') {
        this._activateTab((this._index - 1 + this._tabs.length) % this._tabs.length);
        this._tabs[this._index].focus();
      }
    });

    // Keep underline in sync when the tab bar is manually swiped on mobile
    this._tabsNav && this._tabsNav.addEventListener('scroll', () => {
      this._moveUnderline();
    }, { passive: true });

    // Build SliderControllers — don't call resize() here, track.clientWidth is 0 at this point
    this._sliders = this._panels.map((panel) => {
      const track = panel.querySelector('[data-slider]');
      const prev  = panel.querySelector('.tabbed-collections__arrow--prev');
      const next  = panel.querySelector('.tabbed-collections__arrow--next');
      return track ? new SliderController(track, prev, next, this._columns) : null;
    });

    // Wait for first paint so clientWidth is available, then size cards + position underline
    requestAnimationFrame(() => {
      this._moveUnderline();
      this._sliders.forEach(s => s && s.resize());
    });

    // Re-run on container resize (window resize, theme editor width change)
    this._ro = new ResizeObserver(() => {
      this._moveUnderline();
      this._sliders.forEach(s => s && s.resize());
    });
    this._ro.observe(this);
  }

  disconnectedCallback() {
    this._ro && this._ro.disconnect();
    this._sliders && this._sliders.forEach(s => s && s.destroy());
  }

  _activateTab(index) {
    this._index = index;

    this._tabs.forEach((tab, i) => {
      const on = i === index;
      tab.classList.toggle('active', on);
      tab.setAttribute('aria-selected', String(on));
    });

    this._panels.forEach((panel, i) => {
      panel.classList.remove('active');
      if (i === index) {
        // rAF re-triggers the CSS fade-up animation and ensures the panel
        // has display:block before we read its track dimensions
        requestAnimationFrame(() => {
          panel.classList.add('active');
          this._sliders[i] && this._sliders[i].resize();
        });
      }
    });

    this._moveUnderline();
    this._scrollTabIntoView(this._tabs[index]);
  }

  _moveUnderline() {
    const tab = this._tabs[this._index];
    if (!tab || !this._underline || !this._tabsNav) return;
    // offsetLeft is relative to the scroll container, so subtract scrollLeft
    // to get the visual position of the underline within the track
    const left = tab.offsetLeft - this._tabsNav.scrollLeft;
    this._underline.style.left  = left + 'px';
    this._underline.style.width = tab.offsetWidth + 'px';
  }

  _scrollTabIntoView(tab) {
    if (!tab || !this._tabsNav) return;
    const navRect = this._tabsNav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const offset  = tabRect.left - navRect.left;
    if (offset < 0 || offset + tabRect.width > navRect.width) {
      this._tabsNav.scrollBy({ left: offset - 16, behavior: 'smooth' });
    }
  }
}

/* ============================================================
   SliderController
   Manages one product track. Card widths are set via JS so
   exactly N cards are visible at each breakpoint.
   ============================================================ */
class SliderController {
  constructor(track, prevBtn, nextBtn, desktopColumns) {
    this.track          = track;
    this.prevBtn        = prevBtn;
    this.nextBtn        = nextBtn;
    this.desktopColumns = desktopColumns || 4;
    this.GAP            = 16; // matches CSS gap value on .tabbed-collections__track

    // Reveal arrows now that JS is active
    if (this.prevBtn) this.prevBtn.hidden = false;
    if (this.nextBtn) this.nextBtn.hidden = false;

    // Arrow clicks scroll by one card width
    this.prevBtn && this.prevBtn.addEventListener('click', () => this._slide(-1));
    this.nextBtn && this.nextBtn.addEventListener('click', () => this._slide(1));

    // Scroll events update button disabled state
    this._onScroll = this._updateButtons.bind(this);
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    // NOTE: do NOT call this.resize() here — track.clientWidth is 0 until painted.
    // The parent TabbedCollections calls resize() after requestAnimationFrame.
  }

  _visibleColumns() {
    const vw = window.innerWidth;
    if (vw <= 749) return 2;
    if (vw <= 989) return Math.max(2, this.desktopColumns - 1);
    return this.desktopColumns;
  }

  resize() {
    const cols  = this._visibleColumns();
    const total = this.track.clientWidth;

    // Guard: if track still has no width (e.g. panel is display:none), bail
    if (total === 0) return;

    const cardW = Math.floor((total - (cols - 1) * this.GAP) / cols);

    Array.from(this.track.children).forEach(card => {
      card.style.width     = cardW + 'px';
      card.style.flexShrink = '0';
    });

    this._updateButtons();
  }

  _slide(dir) {
    const first = this.track.firstElementChild;
    if (!first) return;
    const step = first.offsetWidth + this.GAP;
    this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  _updateButtons() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 2;
    const atEnd   = scrollLeft + clientWidth >= scrollWidth - 2;

    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', String(atStart));
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', String(atEnd));
    }
  }

  destroy() {
    this.track.removeEventListener('scroll', this._onScroll);
  }
}

if (!customElements.get('tabbed-collections')) {
  customElements.define('tabbed-collections', TabbedCollections);
}